import { useMemo } from "react";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { flattenCssVariables } from "@circle-react-shared/BuilderUI/Editor/utils/flattenCssVariables";
import { CORE_COLORS } from "../SiteStyles/Tokens/Colors/constants";
import { extractCssVariables } from "../SiteStyles/Tokens/utils/extractCssVariables";
import { extractCssVariablesFromTheme } from "../SiteStyles/Tokens/utils/extractCssVariablesFromTheme";
import { generateNextColorKey } from "../utils/generateNextColorKey";

export const useCustomColors = () => {
  // Combined subscription for all custom colors state
  const {
    currentCanvasTheme,
    appearance,
    updateCustomColors,
    onAddCustomColor,
  } = useBuilderStore(state => ({
    currentCanvasTheme: state.currentCanvasTheme,
    appearance: state.appearance,
    updateCustomColors: state.updateCustomColors,
    onAddCustomColor: state.onAddCustomColor,
  }));

  // Memoize derived values
  const customColors = useMemo(
    () => currentCanvasTheme?.customColors ?? {},
    [currentCanvasTheme?.customColors],
  );

  const cssVariables = useMemo(
    () => currentCanvasTheme?.cssVariables ?? {},
    [currentCanvasTheme?.cssVariables],
  );

  const flattenedCssVariables = useMemo(
    () => flattenCssVariables(cssVariables, appearance),
    [cssVariables, appearance],
  );

  const handleAddCustomColor = async (color: string) => {
    const newColorKey = generateNextColorKey(customColors);

    try {
      await onAddCustomColor?.({ ...customColors, [newColorKey]: color });
    } catch (error) {
      console.error(error);
    } finally {
      updateCustomColors({ ...customColors, [newColorKey]: color });
    }
  };

  // Memoize expensive computations
  const coreColorsCssVariables = useMemo(
    () => extractCssVariablesFromTheme(CORE_COLORS),
    [],
  );

  const themeColors = useMemo(
    () => extractCssVariables(flattenedCssVariables, coreColorsCssVariables),
    [flattenedCssVariables, coreColorsCssVariables],
  );

  return {
    customColors,
    handleAddCustomColor,
    themeColors,
    flattenedCssVariables,
  };
};
